import AuthService from "../services/AuthService";
import {
  ArchieStreamEvent,
  ArchieStreamRequest,
} from "../types/archie/archieStream";

/**
 * SSE client for the Archie streaming chat endpoint (CK-2203/CK-2215).
 *
 * Uses fetch + ReadableStream (NOT axios — its XHR adapter cannot stream in browsers) and
 * deliberately bypasses helpers/axiosInstance: its isApiRequest host check only attaches auth for
 * ApiUrl-prefixed requests, which would silently strip auth here. Archie's protocol carries the
 * Cognito id token in `x-id-token` (Authorization is reserved for the SigV4 signature added by
 * CloudFront OAC in cloud / the signing proxy in local dev).
 */

export class ArchieStreamError extends Error {
  statusCode: number;
  constructor(message: string, statusCode: number) {
    super(message);
    this.name = "ArchieStreamError";
    this.statusCode = statusCode;
  }
}

/**
 * Parse complete SSE frames out of an accumulating buffer.
 * Frames are separated by a blank line; each frame has `event:` and `data:` lines. Frames without
 * data (Archie's ~32KB `:`-comment FLUSH_PAD keep-alives and 10s heartbeats) are dropped here —
 * that is the entire keep-alive handling. Malformed JSON is dropped, never fatal.
 */
export const parseSse = (
  buffer: string
): { events: ArchieStreamEvent[]; remainder: string } => {
  const frames = buffer.split("\n\n");
  const remainder = frames.pop() ?? "";
  const events: ArchieStreamEvent[] = [];
  for (const frame of frames) {
    let type = "";
    let data = "";
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) type = line.slice(6).trim();
      else if (line.startsWith("data:")) data += line.slice(5).trim();
      // any other line (e.g. ":" comment padding) is ignored
    }
    if (!type || !data) continue;
    try {
      events.push({ type, data: JSON.parse(data) });
    } catch {
      // malformed frame — drop it
    }
  }
  return { events, remainder };
};

/** Hex SHA-256 of a string (crypto.subtle is available: the app is HTTPS / localhost). */
const sha256Hex = async (text: string): Promise<string> => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(text)
  );
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

export interface StreamArchieChatArgs {
  /** Trailing-slash-free base — relative "/archie" in cloud, absolute URL in local dev. */
  baseUrl: string;
  request: ArchieStreamRequest;
  signal?: AbortSignal;
  onEvent: (event: ArchieStreamEvent) => void;
}

/**
 * POST {baseUrl}/chat and dispatch every SSE event to onEvent until the stream ends.
 * Throws ArchieStreamError on a pre-stream HTTP failure; AbortError propagates to the caller.
 */
export const streamArchieChat = async ({
  baseUrl,
  request,
  signal,
  onEvent,
}: StreamArchieChatArgs): Promise<void> => {
  const body = JSON.stringify(request);
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Id-Token": await AuthService.getInstance().getIdToken(),
  };
  // CloudFront OAC signs the request to the Lambda Function URL but does not hash the body:
  // POSTs through the same-origin /archie path must carry the payload hash or the origin 403s.
  // Omitted for absolute (cross-origin, local-dev proxy) bases — the proxy hashes it itself and
  // the header is not in the Lambda's CORS allow-list.
  if (baseUrl.startsWith("/")) {
    headers["x-amz-content-sha256"] = await sha256Hex(body);
  }

  const response = await fetch(`${baseUrl}/chat`, {
    method: "POST",
    headers,
    body,
    signal,
  });

  if (!response.ok) {
    let message = `Archie request failed (${response.status})`;
    try {
      const parsed = await response.json();
      if (parsed?.error) message = parsed.error;
    } catch {
      // non-JSON error body — keep the default message
    }
    throw new ArchieStreamError(message, response.status);
  }
  // Guard against the SPA's CloudFront 404->index.html rewrite handing us HTML.
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("text/event-stream") || !response.body) {
    throw new ArchieStreamError(
      "Archie endpoint did not return an event stream.",
      502
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, remainder } = parseSse(buffer);
    buffer = remainder;
    for (const event of events) onEvent(event);
  }
};
